import { useState } from 'react';
import {
  DAY_END_MIN,
  DAY_START_MIN,
  PX_PER_MIN,
  WEEKDAY_NAMES,
  addDays,
  parseDateKey,
  toDateKey,
  toWeekKey,
  weekKeyToMonday,
} from '@skolveckan/shared';
import { EmptyState } from '../components/EmptyState';
import { hourMarks } from '../components/DayTimeline';
import { IconChevronLeft, IconChevronRight } from '../components/Icons';
import { LessonBlock, phaseOf } from '../components/LessonBlock';
import { LessonModal } from '../components/LessonModal';
import { NowLine } from '../components/NowLine';
import { useApi } from '../hooks/useApi';
import { nowMinutes, useNow } from '../hooks/useNow';
import { api } from '../lib/api';
import { subjectColors } from '../lib/subjects';

export function WeekPage() {
  const now = useNow();
  const nowMin = nowMinutes(now);
  const today = toDateKey(now);

  const [weekKey, setWeekKey] = useState(() => toWeekKey(now));
  const [openLessonId, setOpenLessonId] = useState<string | null>(null);

  const week = useApi(() => api.week(weekKey), [weekKey]);
  const height = (DAY_END_MIN - DAY_START_MIN) * PX_PER_MIN;

  function step(delta: number) {
    const monday = weekKeyToMonday(weekKey);
    setWeekKey(toWeekKey(addDays(monday, delta * 7)));
  }

  const days = week.data?.days ?? [];
  const isEmpty = days.every((d) => d.lessons.length === 0);

  return (
    <>
      <header className="page__head">
        <h1>Vecka {weekKey.split('-W')[1]}</h1>
        {days.length > 0 && (
          <span className="muted">
            {formatSpan(days[0]!.date, days[days.length - 1]!.date)}
          </span>
        )}
        <div className="grow" />
        <button
          type="button"
          className="button button--ghost button--icon"
          onClick={() => step(-1)}
          aria-label="Föregående vecka"
        >
          <IconChevronLeft />
        </button>
        <button
          type="button"
          className="button button--ghost"
          onClick={() => setWeekKey(toWeekKey(new Date()))}
        >
          Denna vecka
        </button>
        <button
          type="button"
          className="button button--ghost button--icon"
          onClick={() => step(1)}
          aria-label="Nästa vecka"
        >
          <IconChevronRight />
        </button>
      </header>

      <section className="card panel panel--grow">
        {week.loading && <p className="muted">Hämtar veckan…</p>}

        {!week.loading && isEmpty && (
          <EmptyState
            title="Inget schema den här veckan"
            body="Lägg in klassens lektioner i adminvyn, så dyker de upp här."
          />
        )}

        {!isEmpty && (
          <>
            <div className="weekgrid__header">
              <div className="weekgrid__gutter" />
              {days.map((day) => (
                <div
                  key={day.date}
                  className={`weekgrid__dayname${day.date === today ? ' weekgrid__dayname--today' : ''}`}
                >
                  <span>{capitalize(WEEKDAY_NAMES[day.weekday - 1] ?? '')}</span>
                  <span className="weekgrid__daydate">
                    {formatShort(day.date)}
                    {day.date === today ? ' · idag' : ''}
                  </span>
                </div>
              ))}
            </div>

            <div className="weekgrid" style={{ height }}>
              <div className="weekgrid__gutter weekgrid__gutter--times">
                {hourMarks().map((m) => (
                  <span
                    key={m}
                    className="timeline__hourlabel timeline__hourlabel--absolute"
                    style={{ top: (m - DAY_START_MIN) * PX_PER_MIN - 7 }}
                  >
                    {String(Math.floor(m / 60)).padStart(2, '0')}:00
                  </span>
                ))}
              </div>

              {days.map((day) => {
                const isToday = day.date === today;
                return (
                  <div
                    key={day.date}
                    className={`weekgrid__col${isToday ? ' weekgrid__col--today' : ''}`}
                  >
                    {day.lessons.map((lesson) => (
                      <LessonBlock
                        key={lesson.id}
                        lesson={lesson}
                        phase={phaseOf(lesson, nowMin, isToday)}
                        compact
                        onOpen={() => setOpenLessonId(lesson.id)}
                      />
                    ))}
                    {isToday && <NowLine nowMin={nowMin} showLabel={false} />}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </section>

      {(week.data?.upcoming.length ?? 0) > 0 && (
        <section className="card upcoming">
          <span className="label">Kommande</span>
          {week.data!.upcoming.map((item) => {
            const colors = subjectColors(item.colorKey);
            return (
              <span key={item.assignmentId} className="upcoming__item">
                <span className="dot" style={{ background: colors.line }} />
                <strong>{item.dueDate ? formatShort(item.dueDate) : 'Snart'}</strong>
                <span>{item.title}</span>
              </span>
            );
          })}
        </section>
      )}

      <LessonModal lessonId={openLessonId} onClose={() => setOpenLessonId(null)} />
    </>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatShort(dateKey: string): string {
  const d = parseDateKey(dateKey);
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

function formatSpan(from: string, to: string): string {
  return `${formatShort(from)} – ${formatShort(to)}`;
}
